import { liftOnHover, slideInFromBottom, slideInFromLeft, slideInFromRight, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Info } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '../ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

type PricingPlan = {
  name: string;
  description: string;
  price: string;
  priceDetail: string;
  features: string[];
  buttonLabel: string;
  buttonDisabled?: boolean;
  highlighted?: boolean;
};

const plans: PricingPlan[] = [
  {
    name: 'Free',
    description: 'For sellers who want to list their home with zero cost and get started quickly.',
    price: '0% Commission',
    priceDetail: '$0',
    features: [
      'Get your home listed on Swim Homes',
      'Reach potential buyers on our platform'
    ],
    buttonLabel: 'Get Started',
  },
  {
    name: 'Core',
    description: 'For sellers who want to maximize their listing potential',
    price: '0% Commission',
    priceDetail: '$599',
    highlighted: true,
    features: [
      '24/7 AI Agent Support—The Captain is always on deck',
      'Photos, 3D Tours, Staging, Open House—DIY with guidance',
      'Listing on MLS, Zillow, Redfin, Realtor.com with unlimited changes',
      'AI-guided closing support',
      'Digital Document Management',
      'e-Signature for Offers'
    ],
    buttonLabel: 'Get Started',
  },
  {
    name: 'Premium',
    description: 'For sellers who want expert support and maximum exposure.',
    price: '0% Commission',
    priceDetail: '$1,499',
    features: [
      'Everything in Core, plus:',
      'Professional Photography Session',
      'Lock Box & For Sale Yard Sign',
      'Comparative Market Analysis',
      'Premium Support with Priority Response'
    ],
    buttonLabel: 'Get Started',
  },
];

function PricingCard({ plan, index }: { plan: PricingPlan; index: number }) {
  const cardClasses = plan.highlighted
    ? 'relative h-full bg-brand text-white border-0 shadow-xl shadow-brand/20 ring-2 ring-brand-light/30 scale-105'
    : 'relative h-full bg-white hover:border-brand-light/30 hover:shadow-lg transition-all duration-300';

  const displayPrice = plan.priceDetail;
  const displayDetail = plan.price;

  const getCardVariants = () => {
    if (index === 0) return slideInFromLeft;
    if (index === 2) return slideInFromRight;
    return slideInFromBottom;
  };

  return (
    <motion.div
      className="flex"
      variants={getCardVariants()}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOptions}
    >
      <motion.div
        className="flex-1"
        variants={liftOnHover}
        initial="rest"
        whileHover="hover"
      >
        <Link to="/dashboard/edit" className="block h-full">
          <Card className={cn(cardClasses, "flex flex-col h-full cursor-pointer")}>
            <CardHeader className="space-y-4">
            <h3 className={`text-3xl font-bold tracking-tight ${plan.highlighted ? 'text-white' : 'text-black'}`}>
              {plan.name}
            </h3>
            <p className={`text-base ${plan.highlighted ? 'text-brand-lighter' : 'text-gray-700'}`}>
              {plan.description}
            </p>
            <div className="pt-4">
              <div className="flex items-baseline gap-2">
                <span className={`text-4xl font-bold tracking-tight ${plan.highlighted ? 'text-white' : 'text-black'}`}>
                  {displayPrice}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xl font-bold tracking-tight ${plan.highlighted ? 'text-white' : 'text-black'}`}>
                  {displayDetail}
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className={`h-4 w-4 ml-2 inline ${plan.highlighted ? 'text-brand-lighter' : 'text-gray-500'}`} />
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        sideOffset={5}
                        className="max-w-[250px] bg-white text-black p-3 rounded-lg shadow-lg border border-gray-200"
                      >
                        <p>Zero listing commission, still subject to buyer's agent fees, typically between 1-2.5%</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              </div>
            </div>
          </CardHeader>

          <div className={`mx-6 h-px ${plan.highlighted ? 'bg-brand' : 'bg-gray-200'}`} />

          <CardContent className="pt-6 space-y-6 grow">
            <div className="space-y-4">
              {plan.features.map((feature, featureIndex) => (
                <div key={featureIndex} className="flex items-start gap-3">
                  <CheckCircle className={`h-5 w-5 shrink-0 mt-0.5 ${plan.highlighted ? 'text-brand-lighter' : 'text-brand'
                    }`} />
                  <span className={`text-[15px] font-semibold leading-tight ${plan.highlighted ? 'text-white' : 'text-gray-700'
                    }`}>{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="pt-6 mt-auto">
            <div className="w-full text-center">
              <span className={`inline-flex items-center text-lg font-semibold ${
                plan.highlighted ? 'text-white' : 'text-brand'
              }`}>
                {plan.buttonLabel}
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </div>
          </CardFooter>
          </Card>
        </Link>
      </motion.div>
    </motion.div>
  );
}

export default function ProductCards() {
  return (
    <section key={2222}>
      <div className="flex flex-col items-center justify-center">
        <motion.div
          className="max-w-3xl mx-auto text-center mb-16"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions}
        >
          <motion.span className="landing-badge" variants={staggerItem}>Pricing</motion.span>
          <motion.h2 className="landing-title" variants={staggerItem}>
            Selling Made Simple: Choose Your Plan
          </motion.h2>
          <motion.p className="landing-subtitle" variants={staggerItem}>
            Ready to make a splash with your sale?
          </motion.p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-6xl mx-auto items-stretch px-4 overflow-x-visible"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions}
        >
          {plans.map((plan, idx) => (
            <PricingCard key={idx} plan={plan} index={idx} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};